import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, Pencil } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { AdvertiseLayout } from '@/components/advertise/AdvertiseLayout';
import { AdvertiseSideSummary } from '@/components/advertise/AdvertiseSideSummary';
import { AdvertiseFooterNav } from '@/components/advertise/AdvertiseFooterNav';
import { useAdvertise } from '@/store/useAdvertise';
import { useStepGuard, sellersStepValid } from '@/utils/advertiseGuards';
import { SELLERS_STEPS } from '@/config/advertiseSteps';
import { getBoostQuote } from '@/services/advertiseApi';
import { formatPrice } from '@/utils/format';
import { toast } from 'sonner';

const ROLE_LABEL = { owner: 'Homeowner', 'listing-agent': 'Listing agent', 'co-agent': 'Co-agent' };

const Row = ({ label, value, editTo }) => (
  <div className="flex items-start justify-between gap-3 border-b border-slate-100 py-2.5 last:border-0">
    <dt className="text-sm text-slate-500">{label}</dt>
    <dd className="flex items-center gap-2 text-right text-sm font-medium text-slate-900">
      {value || '—'}
      {editTo && <Link to={editTo} className="text-blue-600"><Pencil size={13} /></Link>}
    </dd>
  </div>
);

export default function SellersReview() {
  const navigate = useNavigate();
  const sellers = useAdvertise((s) => s.sellers);
  const setField = useAdvertise((s) => s.setField);
  useStepGuard(sellersStepValid.review(sellers), '/advertise/sellers');
  const [loadingQuote, setLoadingQuote] = useState(!sellers.quoteId);
  const [quoteError, setQuoteError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (sellers.quoteId) {
      setLoadingQuote(false);
      setQuoteError(false);
      return;
    }
    let alive = true;
    setLoadingQuote(true);
    setQuoteError(false);
    const listPrice = sellers.listPrice || sellers.matchedListing?.price;
    getBoostQuote({ address: sellers.address, listPrice })
      .then((q) => {
        if (!alive) return;
        setField('sellers', { quoteId: q.quote_id, quoteAmount: q.amount });
        setLoadingQuote(false);
      })
      .catch(() => {
        if (!alive) return;
        setQuoteError(true);
        setLoadingQuote(false);
        toast.error('Could not calculate your price. Please try again.');
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryKey]);

  const fullAddress = [sellers.address, sellers.city, sellers.state, sellers.zip].filter(Boolean).join(', ');

  return (
    <AdvertiseLayout
      trackLabel="Listing Boost"
      steps={SELLERS_STEPS}
      currentStepId="review"
      title="Review your Listing Boost"
      subtitle="One flat fee, active until your home is sold."
      sidebar={
        <AdvertiseSideSummary
          title="Order summary"
          items={[{ label: 'Listing Boost', value: loadingQuote ? 'Calculating…' : formatPrice(sellers.quoteAmount) }]}
          footnote="Charged once — no recurring fees."
        />
      }
      footer={
        <AdvertiseFooterNav
          onBack={() => navigate('/advertise/sellers/contact')}
          onNext={() => navigate('/advertise/sellers/checkout')}
          nextDisabled={!sellers.consent || loadingQuote || quoteError || !sellers.quoteId}
          nextLabel="Continue to payment"
        />
      }
    >
      {loadingQuote ? (
        <div className="flex items-center gap-2 py-6 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" />Calculating your price…</div>
      ) : quoteError ? (
        <div className="flex flex-col items-start gap-3 py-6">
          <p className="text-sm text-slate-600">We could not calculate your boost price.</p>
          <Button variant="outline" className="rounded-xl" onClick={() => setRetryKey((k) => k + 1)}>Try again</Button>
        </div>
      ) : (
        <dl>
          <Row label="Address" value={fullAddress} editTo="/advertise/sellers/address" />
          <Row label="Role" value={ROLE_LABEL[sellers.role]} editTo="/advertise/sellers/details" />
          <Row label="Contact" value={[sellers.firstName, sellers.lastName].filter(Boolean).join(' ')} editTo="/advertise/sellers/contact" />
          <Row label="Email" value={sellers.email} editTo="/advertise/sellers/contact" />
          <Row label="Listing Boost price" value={formatPrice(sellers.quoteAmount)} />
        </dl>
      )}

      <label className="mt-6 flex cursor-pointer items-start gap-2.5">
        <Checkbox checked={sellers.consent} onCheckedChange={(v) => setField('sellers', { consent: !!v })} className="mt-0.5" data-testid="sellers-review-consent" />
        <span className="text-sm text-slate-600">
          I agree to the <Link to="/terms" className="text-blue-600 underline">Terms</Link> and <Link to="/privacy" className="text-blue-600 underline">Privacy Policy</Link>, and authorize casahom to charge the amount above.
        </span>
      </label>
    </AdvertiseLayout>
  );
}
